import { useEffect } from 'react'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { DealIcon } from '@shipt/design-system-icons'
import { Button, IconButton } from '@shipt/design-system-buttons'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { useCanShowViewType } from '@/features/shared/services/ViewedElementTracker/hooks'
import { useQueryViewedElementTracker } from '@/features/shared/services/ViewedElementTracker/queries'
import { getBoxShadow } from '@/features/shared/styles/effects'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicActivityPunchCardProgressDrawer } from '@/features/shop/components/Drawer/ActivityPunchCardProgressDrawer/DynamicActivityPunchCardProgressDrawer'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useQueryCMSActivityPunchCard } from '@/features/cms/services/CMS/queries'
import { theme } from '@/features/shared/constants/shiptTheme'

export const ActivityPunchCardFab = () => (
  // this is rendered on the client side due to using useMediaQuery, which uses window.matchMedia.
  <ClientOnly>
    <MobileActivityPunchCardFab />
  </ClientOnly>
)

const MobileActivityPunchCardFab = () => {
  const { screenSm: isMobile } = useMediaQuery()

  return isMobile ? <ActivityPunchCardFabContent /> : null
}

const ActivityPunchCardFabContent = () => {
  const { openDialog } = useHandleDialog()
  const { data: activityPunchCardData } = useQueryCMSActivityPunchCard()
  const callout = activityPunchCardData?.data?.callout

  const [, setSeenActivityPunchCardFab] = useCanShowViewType('activity_punchcard_fab')
  const { data: viewedTrackerResponse, isFetched: hasFetchedViewedTracker } =
    useQueryViewedElementTracker()
  const shouldShowCondensedFab =
    viewedTrackerResponse?.activity_punchcard_fab?.should_show === false

  useEffect(() => {
    if (!hasFetchedViewedTracker || !callout) return
    trackElementViewed({
      type: 'button',
      content: `quest-id-${activityPunchCardData.metadata.read_operation_info?.source_object.object.quest_id}`,
      display_sublocation: `${activityPunchCardData.analytic_slug}`,
    })
  }, [activityPunchCardData, callout, hasFetchedViewedTracker])

  if (!hasFetchedViewedTracker || !callout) return null

  const fabAriaLabel = `${callout}. Open to view details.`

  const handleFabClick = () => {
    setSeenActivityPunchCardFab()

    trackElementClicked({
      type: 'button',
      content: `quest-id-${activityPunchCardData.metadata.read_operation_info?.source_object.object.quest_id}`,
      display_sublocation: `${activityPunchCardData.analytic_slug}`,
    })
    openDialog(DynamicActivityPunchCardProgressDrawer)
  }

  if (!shouldShowCondensedFab) {
    return (
      <FullMobileFab
        leadingIcon={DealIcon}
        surface="inverse"
        onClick={handleFabClick}
        aria-label={fabAriaLabel}
      >
        {callout}
      </FullMobileFab>
    )
  }

  return (
    <CondensedMobileFab
      icon={DealIcon}
      surface="inverse"
      onClick={handleFabClick}
      aria-label={fabAriaLabel}
    />
  )
}

const SharedButtonStyles = css`
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 999;
  display: flex;
  ${getBoxShadow('float', 'light')};

  &:hover,
  &:focus-visible,
  &:active {
    background-color: ${theme.color.background.base.neutral.default};
  }

  & svg {
    color: ${theme.color.content.feedback.promotion.primary_on_soft};
  }
`

const FullMobileFab = styled(Button)`
  ${SharedButtonStyles}
`

const CondensedMobileFab = styled(IconButton)`
  ${SharedButtonStyles}
`
